import type { LeadsQuery } from './LeadsPage';

interface Campaign {
  id: string;
  name: string;
}

interface LeadFiltersProps {
  filters: LeadsQuery;
  onChange: (filters: LeadsQuery) => void;
  campaigns: Campaign[];
}

const LEAD_STATES = [
  'awaiting_eval',
  'dedup_hubspot',
  'route_decision',
  'mail_running',
  'mail_replied',
  'mail_no_reply',
  'mail_bounced',
  'mail_unsubscribed',
  'formfill_running',
  'formfill_done',
  'awaiting_form_reply',
  'formfill_no_reply',
  'formfill_failed',
  'qualified_via_mail',
  'qualified_via_form',
  'call_queued',
  'bigcall_pushed',
  'call_in_progress',
  'meeting_booked',
  'not_interested',
  'nrp_max',
  'opt_out',
  'skip_disqualified',
];

export function LeadFilters({ filters, onChange, campaigns }: LeadFiltersProps) {
  function toggleState(state: string) {
    const current = filters.state ?? [];
    const next = current.includes(state)
      ? current.filter((s) => s !== state)
      : [...current, state];
    onChange({ ...filters, state: next.length ? next : undefined });
  }

  function toggleCampaign(id: string) {
    const current = filters.campaign_id ?? [];
    const next = current.includes(id)
      ? current.filter((c) => c !== id)
      : [...current, id];
    onChange({ ...filters, campaign_id: next.length ? next : undefined });
  }

  return (
    <aside className="w-56 flex-shrink-0 space-y-5">
      {/* Campaigns */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Campagnes</p>
        <ul className="space-y-1 max-h-40 overflow-y-auto">
          {campaigns.map((c) => (
            <li key={c.id}>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-brand-primary"
                  checked={(filters.campaign_id ?? []).includes(c.id)}
                  onChange={() => toggleCampaign(c.id)}
                />
                <span className="truncate">{c.name}</span>
              </label>
            </li>
          ))}
        </ul>
      </div>

      {/* States */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">État</p>
        <ul className="space-y-1 max-h-64 overflow-y-auto">
          {LEAD_STATES.map((s) => (
            <li key={s}>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-brand-primary"
                  checked={(filters.state ?? []).includes(s)}
                  onChange={() => toggleState(s)}
                />
                <span className="font-mono">{s}</span>
              </label>
            </li>
          ))}
        </ul>
      </div>

      {/* Has email */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Champs</p>
        <div className="space-y-1">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="rounded border-gray-300 text-brand-primary"
              checked={filters.has_email === 'true'}
              onChange={(e) =>
                onChange({ ...filters, has_email: e.target.checked ? 'true' : undefined })
              }
            />
            A un email
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="rounded border-gray-300 text-brand-primary"
              checked={filters.has_phone === 'true'}
              onChange={(e) =>
                onChange({ ...filters, has_phone: e.target.checked ? 'true' : undefined })
              }
            />
            A un téléphone
          </label>
        </div>
      </div>

      {/* Updated in X days */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
          Mis à jour (jours)
        </p>
        <select
          className="input w-full text-sm"
          value={filters.updated_days ?? ''}
          onChange={(e) => {
            const val = e.target.value;
            if (!val) {
              const { updated_days: _, ...rest } = filters;
              onChange(rest);
            } else {
              onChange({ ...filters, updated_days: val });
            }
          }}
        >
          <option value="">Tous</option>
          <option value="1">1 jour</option>
          <option value="7">7 jours</option>
          <option value="14">14 jours</option>
          <option value="30">30 jours</option>
        </select>
      </div>

      {/* Reset */}
      <button
        type="button"
        className="btn-ghost text-xs w-full"
        onClick={() => onChange({})}
      >
        Réinitialiser
      </button>
    </aside>
  );
}
